import React, { useState, useEffect } from "react";
import api from "../../api";

const LuckyDipModal = ({ isOpen, toggle, playerPos, spawnText }) => {
  const [status, setStatus] = useState(null);
  const [rewards, setRewards] = useState({ pokemon: [], item_images: [] });
  const [currentRewardIndex, setCurrentRewardIndex] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [showRollingRewards, setShowRollingRewards] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchStatus();
      fetchRewards();
    }
  }, [isOpen]);

  useEffect(() => {
    let interval;
    if (isOpen && rewards.pokemon.length + rewards.item_images.length > 0) {
      interval = setInterval(() => {
        const total = rewards.pokemon.length + rewards.item_images.length;
        setCurrentRewardIndex((prevIndex) => (prevIndex + 1) % total);
      }, 500);
    }
    return () => clearInterval(interval);
  }, [isOpen, rewards]);

  const fetchStatus = async () => {
    try {
      const res = await api.get("/v1/lucky-dip/status", { withCredentials: true });
      setStatus(res.data);
    } catch (err) {
      console.error("Failed to fetch status:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRewards = async () => {
    try {
      const res = await api.get("/v1/lucky-dip/rewards", { withCredentials: true });
      setRewards(res.data);
    } catch (err) {
      console.error("Failed to fetch rewards:", err);
    }
  };

  const handleLuckyDip = async () => {
    setIsFetching(true);
    setShowRollingRewards(true);
    
    try {
      const res = await api.post("/v1/lucky-dip", {}, { withCredentials: true });
      const reward = res.data.reward;
      setShowRollingRewards(false);
      
      // Close modal immediately
      toggle();
      
      // Show floating text for successful dip
      if (reward.type === "pokemon") {
        spawnText(`You found ${reward.name}!`, playerPos.x, playerPos.y - 20, "#4CAF50");
      } else if (reward.type === "item") {
        const itemName = reward.image.split("/").pop().replace(".png", "");
        spawnText(`You found ${itemName}!`, playerPos.x, playerPos.y - 20, "#4CAF50");
      } else {
        spawnText(res.data.message, playerPos.x, playerPos.y - 20, "#4CAF50");
      }
      
      fetchStatus();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || "Lucky Dip failed.";
      spawnText(errorMessage, playerPos.x, playerPos.y - 20, "#f44336");
    } finally {
      setIsFetching(false);
      setShowRollingRewards(false);
    }
  };

  const renderRewardDisplay = () => {
    const total = rewards.pokemon.length + rewards.item_images.length;
    if (total === 0) return <p>Loading rewards...</p>;

    if (currentRewardIndex < rewards.pokemon.length) {
      const name = rewards.pokemon[currentRewardIndex];
      return (
        <div style={{ textAlign: 'center' }}>
          <img 
            src={`/sprites/${name}.png`} 
            alt={name} 
            style={{ 
              width: '100px',
              imageRendering: 'pixelated'
            }} 
          />
          <div style={{ marginTop: '8px', fontWeight: 'bold' }}>{name}</div>
        </div>
      );
    } else {
      const index = currentRewardIndex - rewards.pokemon.length;
      const image = rewards.item_images[index];
      const imageName = image.split("/").pop().replace(".png", "");
      return (
        <div style={{ textAlign: 'center' }}>
          <img 
            src={`/${image}`} 
            alt={imageName} 
            style={{ 
              width: '60px',
              imageRendering: 'pixelated'
            }} 
          />
          <div style={{ marginTop: '8px', fontWeight: 'bold' }}>{imageName}</div>
        </div>
      );
    }
  };

  const rewardMilestones = [
    { streak: 2, coins: 10 },
    { streak: 5, coins: 50 },
    { streak: 10, coins: 100 },
    { streak: 20, coins: 150 },
    { streak: 30, coins: 200 },
    { streak: 50, coins: 250 },
    { streak: 100, coins: 300 },
    { streak: 200, coins: 400 },
    { streak: 300, coins: 500 },
    { streak: 500, coins: 600 },
    { streak: 750, coins: 800 },
    { streak: 1000, coins: 1000 },
    { streak: 1250, coins: 1250 },
    { streak: 1500, coins: 1500 },
    { streak: 2000, coins: 2000 },
  ];

  if (!isOpen) return null;

  return (
    <div 
      className="game-popup-overlay"
      onClick={toggle}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px 10px',
        boxSizing: 'border-box',
        zIndex: 1050
      }}
    >
      <div 
        className="lucky-dip-popup"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '95%',
          maxWidth: 900,
          maxHeight: '95vh',
          background: 'linear-gradient(to bottom, rgba(29, 34, 46, 0.95), rgba(41, 50, 65, 0.95))',
          color: '#fff',
          borderRadius: 12,
          backdropFilter: 'blur(10px)',
          border: '2px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          margin: '10px'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '12px 16px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(0, 0, 0, 0.2)',
          flexWrap: 'wrap',
          gap: '8px',
          minHeight: '60px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            flex: '1 1 auto',
            minWidth: '200px'
          }}>
            <h3 style={{ 
              margin: 0, 
              fontSize: '18px', 
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>Snorlax's Lucky Dip</h3>
          </div>
          
          <button
            onClick={toggle}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              color: '#fff',
              borderRadius: '4px',
              padding: '6px 12px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontSize: '14px'
            }}
            onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
            onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
          >
            Close
          </button>
        </div>

        {/* Content */}
        <div style={{
          padding: '16px',
          overflowY: 'auto',
          flex: 1
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div className="spinner-border text-light" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p style={{ marginTop: '10px' }}>Loading lucky dip data...</p>
            </div>
          ) : (
            <>
              {/* Main Info Section */}
              <div style={{
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <img 
                  src="/sprites/Snorlax.png" 
                  alt="Snorlax" 
                  style={{ 
                    width: '150px',
                    imageRendering: 'pixelated',
                    marginBottom: '12px'
                  }} 
                />
                
                <p style={{ fontSize: '14px', lineHeight: '1.5', textAlign: 'center' }}>
                  Legend says there are countries and a massive ecosystem inside Snorlax's massive tummy, 
                  where you can find random Pokémon, items, money etc.
                  <br /><br />
                  Up to once a day, you can dip your hand into his tummy to find something!
                  <br /><br />
                  <strong>Note:</strong> Only use up to two accounts per day to lucky dip!
                </p>

                {status && (
                  <div style={{ marginTop: '12px', textAlign: 'center' }}>
                    <p style={{ 
                      fontSize: '16px', 
                      fontWeight: 'bold',
                      color: '#ffe066',
                      marginBottom: '12px'
                    }}>
                      Current Streak: {status.dip_bonus}
                    </p>
                    
                    {status.vip > Math.floor(Date.now() / 1000) && (
                      <p style={{ 
                        color: '#4CAF50',
                        fontSize: '14px',
                        marginBottom: '12px'
                      }}>
                        As a VIP, you'll receive a bonus of 500 coins!
                      </p>
                    )}
                    
                    <button
                      disabled={status.lucky === 0 || isFetching}
                      onClick={handleLuckyDip}
                      style={{
                        background: status.lucky === 0 || isFetching 
                          ? 'rgba(74, 85, 104, 0.5)' 
                          : 'linear-gradient(to bottom, #4a5568, #2d3748)',
                        border: 'none',
                        color: status.lucky === 0 || isFetching ? '#a0aec0' : '#fff',
                        borderRadius: '4px',
                        padding: '8px 16px',
                        cursor: status.lucky === 0 || isFetching ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                        fontSize: '16px',
                        fontWeight: 'bold'
                      }}
                      onMouseOver={(e) => {
                        if (status.lucky !== 0 && !isFetching) {
                          e.target.style.background = 'linear-gradient(to bottom, #5a6978, #3d4758)';
                          e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.3)';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (status.lucky !== 0 && !isFetching) {
                          e.target.style.background = 'linear-gradient(to bottom, #4a5568, #2d3748)';
                          e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                        }
                      }}
                    >
                      {isFetching ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Dipping...
                        </>
                      ) : status.lucky === 0 ? (
                        "Already Dipped Today"
                      ) : (
                        "Lucky Dip!"
                      )}
                    </button>
                  </div>
                )}
              </div>

              {/* Reward Animation */}
              <div style={{
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <h5 style={{ 
                  fontSize: '16px', 
                  marginBottom: '16px',
                  color: '#ffe066',
                  textAlign: 'center'
                }}>
                  Possible Rewards
                </h5>
                {showRollingRewards ? (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    minHeight: '120px'
                  }}>
                    {renderRewardDisplay()}
                  </div>
                ) : (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    minHeight: '120px'
                  }}>
                    {renderRewardDisplay()}
                  </div>
                )}
              </div>

              {/* Daily Reward Table */}
              <div style={{
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '8px',
                padding: '16px'
              }}>
                <h5 style={{ 
                  fontSize: '16px', 
                  marginBottom: '16px',
                  color: '#ffe066'
                }}>
                  Daily Reward Table
                </h5>
                <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '16px' }}>
                  As a reward for being an active member, there are rewards for taking a lucky dip.
                  <br />
                  Your streak is incremented every time you dip, but for each day you miss, your lucky dip streak will be decreased by 5.
                  <br /><br />
                  <strong>Don't dip 10-15 minutes within midnight (server time), some users report that their dip streak has reduced when doing so.</strong>
                </p>
                
                <div style={{ 
                  maxHeight: '300px', 
                  overflowY: 'auto',
                  background: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: '4px',
                  padding: '8px'
                }}>
                  <table style={{ 
                    width: '100%', 
                    borderCollapse: 'collapse',
                    fontSize: '14px'
                  }}>
                    <thead>
                      <tr>
                        <th style={{ 
                          padding: '8px', 
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                          textAlign: 'center'
                        }}>Streak</th>
                        <th style={{ 
                          padding: '8px', 
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                          textAlign: 'center'
                        }}>Coins</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rewardMilestones.map((milestone, idx) => (
                        <tr 
                          key={idx} 
                          style={{
                            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                            ...(status && status.dip_bonus >= milestone.streak 
                              ? { textDecoration: "line-through", opacity: 0.5 } 
                              : {})
                          }}
                        >
                          <td style={{ 
                            padding: '8px', 
                            textAlign: 'center'
                          }}>{milestone.streak}</td>
                          <td style={{ 
                            padding: '8px',
                            textAlign: 'center',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}>
                            <img
                              src="/items/coin.png"
                              alt="Coins"
                              width="24"
                              style={{ verticalAlign: 'middle' }}
                            />
                            {milestone.coins}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LuckyDipModal;
